import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { FabricCanvas } from '../../types/fabric';
import { Skeleton } from '../Skeleton';
import { useToast } from '../Notifications';
import { BackgroundColorTool } from './BackgroundColorTool';
import { importCatalogAssetAsUpload, type StagedImage } from './StagedImage';
import { Spinner } from '../Spinner';

interface ShapesToolProps {
  canvas: FabricCanvas | null;
}

export function ShapesTool({ canvas }: ShapesToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  // Same staged-then-wizard flow as UploadTool.tsx, per direct user request
  // that picking a shape go through the same vectorize/remove-background/
  // select-colors/crop steps a direct file upload does, instead of dropping
  // straight onto the canvas.
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['shapes'],
    queryFn:  () => fetch('/api/shapes').then(r => r.json()),
    staleTime: 300_000,
  });

  const shapes: any[] = data?.shapes ?? [];
  const markLoaded = (id: string) => setLoadedIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));

  const selectShape = async (shape: any) => {
    setImportingId(shape.id);
    try {
      const result = await importCatalogAssetAsUpload(shape.shapeMainImage);
      if (!result) {
        showToast(t('shapes.importFailed'), 'error');
        return;
      }
      setStaged(result);
    } finally {
      setImportingId(null);
    }
  };

  if (staged) {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{t('shapes.editingShape')}</span>
            <button onClick={() => setStaged(null)} title={t('upload.cancelUpload')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>
              ×
            </button>
          </div>
          {/* key={staged.uploadId} forces a fresh wizard instance per shape —
              same reasoning as UploadTool.tsx's identical key. */}
          <BackgroundColorTool key={staged.uploadId} canvas={canvas} staged={staged} onDone={() => setStaged(null)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 8 }}>
      {!isLoading && shapes.length === 0 ? (
        <p style={{ fontSize: 14, color: 'var(--text-muted, #94a3b8)', padding: 8 }}>{t('shapes.empty')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {shapes.map((shape: any) => (
            <button key={shape.id} onClick={() => selectShape(shape)} disabled={importingId !== null}
              style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 4, background: 'none', cursor: importingId !== null ? 'default' : 'pointer', padding: 4, overflow: 'hidden', aspectRatio: '1' }}>
              {!loadedIds.has(shape.id) && <Skeleton style={{ position: 'absolute', inset: 4 }} />}
              {/* `thumb_url` (small WebP) for the grid; the wizard's staged
                  preview (above) uses the full `shapeMainImage`. */}
              <img
                src={shape.thumb_url ?? shape.shapeMainImage}
                alt={shape.shapeName}
                style={{ width: '100%', height: '100%', objectFit: 'contain', visibility: loadedIds.has(shape.id) ? 'visible' : 'hidden' }}
                loading="lazy"
                onLoad={() => markLoaded(shape.id)}
                onError={() => markLoaded(shape.id)}
              />
              {importingId === shape.id && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)' }}>
                  <Spinner size={18} />
                </div>
              )}
            </button>
          ))}
          {isLoading &&
            Array.from({ length: 9 }).map((_, i) => (
              <div key={`skeleton-${i}`} style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', aspectRatio: '1', padding: 4, boxSizing: 'border-box' }}>
                <Skeleton />
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
